import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  ArrowLeft,
  ArrowsClockwise,
  CheckCircle,
  ClockCounterClockwise,
  CurrencyDollar,
  FloppyDisk,
  Warning,
} from "@phosphor-icons/react";
import { useAction, useMutation, useQuery } from "convex/react";
import { api as convexApi } from "../../convex/_generated/api";
import type { Id } from "../../convex/_generated/dataModel";
import type {
  AiDiagnosticDetail,
  AiDiagnosticSource,
  AiDiagnosticStatus,
  AiDiagnosticSummary,
  AiPricingAdminSetting,
} from "../../shared/aiDiagnostics.ts";
import {
  aggregateAiDiagnostics,
  type AiDiagnosticAggregationResult,
  type AiWorkflowUsageRollup,
} from "../../shared/aiDiagnosticAggregation.ts";
import type { PromptReplaySessionSummary } from "../../shared/promptReplay/types.ts";
import {
  DEFAULT_PROMPT_WORKFLOW,
  isPromptWorkflowKey,
  type PromptWorkflowKey,
} from "../../shared/promptContracts/registry.ts";
import { useRoleAuth } from "../auth/AuthGate.tsx";
import { clearStoredAuth } from "../lib/auth.ts";
import { notificationCopy } from "../lib/notificationCopy.ts";
import { notify } from "../lib/notify.ts";
import type { PromptTemplate } from "./PromptAdminShell.tsx";

type OperatorAuth = {
  role: "operator";
  token: string;
};

type StatusFilter = AiDiagnosticStatus | "all";
type SourceFilter = AiDiagnosticSource | "all";
type WorkflowFilter = PromptWorkflowKey | "all";
type UsageWindow = "15m" | "1h" | "24h" | "custom";
type UsageSort = "tokens" | "cost" | "calls";
type PricingDraft = {
  inputUsdPerMillion: string;
  cachedInputUsdPerMillion: string;
  outputUsdPerMillion: string;
  audioUsdPerHour: string;
};
export function PromptDiagnosticsPage() {
  const auth = useRoleAuth("operator") as OperatorAuth;
  const [searchParams, setSearchParams] = useSearchParams();
  const templates = useQuery(convexApi.promptTemplates.list, { auth }) as PromptTemplate[] | undefined;
  const sessions = useQuery(convexApi.promptReplay.listSessions, { auth }) as PromptReplaySessionSummary[] | undefined;
  const savePricingSetting = useMutation(convexApi.promptDiagnostics.savePricingSetting);
  const aggregateWorkflowUsage = useAction(
    (convexApi as any).promptDiagnosticAggregation.aggregateWorkflowUsage,
  );
  const pricingSettings = useQuery(convexApi.promptDiagnostics.listPricingSettings, { auth }) as AiPricingAdminSetting[] | undefined;
  const [pricingDrafts, setPricingDrafts] = useState<Record<string, PricingDraft>>({});
  const [savingPricingKey, setSavingPricingKey] = useState<string | null>(null);
  const [usageWindow, setUsageWindow] = useState<UsageWindow>("1h");
  const [usageSort, setUsageSort] = useState<UsageSort>("tokens");
  const [customStart, setCustomStart] = useState(() => toDateTimeInput(Date.now() - 60 * 60 * 1_000));
  const [customEnd, setCustomEnd] = useState(() => toDateTimeInput(Date.now()));
  const [usageRollup, setUsageRollup] = useState<AiDiagnosticAggregationResult | null>(null);
  const [usageRollupLoading, setUsageRollupLoading] = useState(false);
  const [usageRollupError, setUsageRollupError] = useState<string | null>(null);
  const [requestedWindow, setRequestedWindow] = useState(() => ({
    startTs: Date.now() - 60 * 60 * 1_000,
    endTs: Date.now(),
  }));
  const rollupRequestId = useRef(0);

  const sessionId = searchParams.get("session") ?? "";
  const workflowParam = searchParams.get("workflow");
  const selectedWorkflow: WorkflowFilter =
    workflowParam === "all" || workflowParam === null
      ? "all"
      : workflowParam && isPromptWorkflowKey(workflowParam)
      ? workflowParam
      : "all";
  const selectedTemplate = useMemo(() => {
    if (selectedWorkflow === "all") return null;
    if (!templates?.length) return null;
    return (
      templates.find((template) => template.workflow === selectedWorkflow) ??
      templates.find((template) => template.workflow === DEFAULT_PROMPT_WORKFLOW) ??
      templates[0]
    );
  }, [selectedWorkflow, templates]);
  const workflow = selectedTemplate?.workflow ?? (selectedWorkflow === "all" ? "all" : selectedWorkflow);
  const workflowLabel = selectedWorkflow === "all"
    ? "All workflows"
    : selectedTemplate?.title ?? workflow;
  const status = normalizeStatus(searchParams.get("status"));
  const source = normalizeSource(searchParams.get("source"));
  const selectedDiagnosticId = searchParams.get("diagnostic") ?? "";
  const diagnostics = useQuery(convexApi.promptDiagnostics.listForWorkflow, {
    auth,
    ...(selectedWorkflow === "all" ? {} : { workflow }),
    ...(sessionId ? { sessionId: sessionId as Id<"sessions"> } : {}),
    ...(status !== "all" ? { status } : {}),
    ...(source !== "all" ? { source } : {}),
    limit: 200,
  }) as AiDiagnosticSummary[] | undefined;
  const selectedSummary =
    diagnostics?.find((diagnostic) => diagnostic.id === selectedDiagnosticId) ??
    diagnostics?.[0] ??
    null;
  const selectedDetail = useQuery(
    convexApi.promptDiagnostics.getDetail,
    selectedSummary
      ? {
          auth,
          diagnosticId: selectedSummary.id as Id<"aiDiagnostics">,
        }
      : "skip",
  ) as AiDiagnosticDetail | null | undefined;

  const totals = useMemo(() => summarizeDiagnostics(diagnostics ?? []), [diagnostics]);
  const workflowLabels = useMemo(() => buildWorkflowLabelMap(templates ?? []), [templates]);
  const fallbackUsageRollup = useMemo<AiDiagnosticAggregationResult | null>(() => {
    if (!diagnostics) return null;
    return {
      ...requestedWindow,
      scannedRowCount: diagnostics.length,
      includedRowCount: diagnostics.length,
      maxScannedRowCount: 200,
      truncated: diagnostics.length >= 200,
      ...aggregateAiDiagnostics(diagnostics),
    };
  }, [diagnostics, requestedWindow]);

  useEffect(() => {
    if (workflowParam === selectedWorkflow) return;
    updateSearch({ workflow: selectedWorkflow }, true);
  }, [selectedWorkflow, workflowParam]);

  useEffect(() => {
    if (!diagnostics) return;
    if (diagnostics.length === 0 && selectedDiagnosticId) {
      updateSearch({ diagnostic: null }, true);
      return;
    }
    if (diagnostics.length > 0 && selectedSummary && selectedSummary.id !== selectedDiagnosticId) {
      updateSearch({ diagnostic: selectedSummary.id }, true);
    }
  }, [diagnostics, selectedDiagnosticId, selectedSummary?.id]);

  useEffect(() => {
    if (!pricingSettings) return;
    setPricingDrafts((current) => {
      const next = { ...current };
      for (const setting of pricingSettings) {
        if (next[setting.key]) continue;
        next[setting.key] = pricingSettingToDraft(setting);
      }
      return next;
    });
  }, [pricingSettings]);

  useEffect(() => {
    void refreshUsageRollup();
  }, [usageWindow, customStart, customEnd, sessionId, auth.token, aggregateWorkflowUsage]);

  async function refreshUsageRollup() {
    const requestId = rollupRequestId.current + 1;
    rollupRequestId.current = requestId;
    setUsageRollupLoading(true);
    setUsageRollupError(null);
    setUsageRollup(null);
    try {
      const window = resolveUsageWindow(usageWindow, customStart, customEnd);
      setRequestedWindow(window);
      const result = await aggregateWorkflowUsage({
        auth,
        ...window,
        ...(sessionId ? { sessionId: sessionId as Id<"sessions"> } : {}),
      }) as AiDiagnosticAggregationResult;
      if (rollupRequestId.current === requestId) setUsageRollup(result);
    } catch {
      if (rollupRequestId.current === requestId) {
        setUsageRollupError(
          "Full-window rollup is unavailable. Showing the filtered recent-list fallback when possible.",
        );
      }
    } finally {
      if (rollupRequestId.current === requestId) setUsageRollupLoading(false);
    }
  }

  function updateSearch(updates: Record<string, string | null>, replace = false) {
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      for (const [key, value] of Object.entries(updates)) {
        if (value === null) next.delete(key);
        else next.set(key, value);
      }
      return next;
    }, { replace });
  }

  function updatePricingDraft(key: string, field: keyof PricingDraft, value: string) {
    setPricingDrafts((current) => ({
      ...current,
      [key]: {
        ...(current[key] ?? {
          inputUsdPerMillion: "",
          cachedInputUsdPerMillion: "",
          outputUsdPerMillion: "",
          audioUsdPerHour: "",
        }),
        [field]: value,
      },
    }));
  }

  async function savePricing(setting: AiPricingAdminSetting) {
    const draft = pricingDrafts[setting.key] ?? pricingSettingToDraft(setting);
    setSavingPricingKey(setting.key);
    try {
      await savePricingSetting({
        auth,
        key: setting.key,
        unit: setting.unit,
        ...(setting.unit === "tokens_per_million"
          ? {
              inputUsdPerMillion: parsePricingNumber(draft.inputUsdPerMillion),
              cachedInputUsdPerMillion: parsePricingNumber(draft.cachedInputUsdPerMillion),
              outputUsdPerMillion: parsePricingNumber(draft.outputUsdPerMillion),
            }
          : {
              audioUsdPerHour: parsePricingNumber(draft.audioUsdPerHour),
            }),
      });
      notify.success(notificationCopy.admin.pricingSaved);
    } catch {
      notify.error(notificationCopy.admin.couldNotSavePricing);
    } finally {
      setSavingPricingKey(null);
    }
  }

  if (!templates) {
    return <div className="loading">Loading prompt diagnostics...</div>;
  }

  if (templates.length === 0) {
    return <div className="loading">No prompt templates are configured.</div>;
  }

  return (
    <div className="operator prompt-admin">
      <header className="topbar">
        <div className="brand">
          <span className="brand-mark" aria-hidden="true" />
          <div className="brand-text">
            <strong title="Conversation Moderator">COMODO</strong>
            <span className="role-badge role-admin" data-testid="role-badge">LLM Diagnostics</span>
          </div>
        </div>
        <div className="top-meta">
          <span className="pill on">{templates.length} workflows</span>
        </div>
        <div className="top-actions">
          <button
            className="btn ghost"
            data-testid="prompt-signout"
            type="button"
            onClick={() => {
              clearStoredAuth();
              window.location.assign("/");
            }}
          >
            Sign out
          </button>
          <Link className="btn ghost sm" to="/admin/llm-settings">
            LLM Settings
          </Link>
          <Link className="btn ghost sm" data-testid="back-to-operator" to="/admin">
            <ArrowLeft aria-hidden="true" weight="bold" />
            Admin View
          </Link>
          <Link className="btn ghost sm" data-testid="open-sessions" to="/admin/sessions">
            Sessions
          </Link>
        </div>
      </header>

      <main className="prompt-diagnostics-main">
        <section className="prompt-editor-shell prompt-diagnostics-shell">
          <div className="prompt-replay prompt-diagnostics" data-testid="prompt-diagnostics">
            <div className="prompt-editor-head">
              <div>
                <p className="prompt-key">
                  llm diagnostics / {selectedWorkflow === "all" ? "all" : workflow}
                </p>
                <h1>Diagnostics</h1>
                <p>
                  Inspect persisted AI requests, errors, token usage, and estimated costs across {workflowLabel.toLowerCase()}.
                </p>
              </div>
              <div className="prompt-replay-stat-strip" aria-label="Diagnostics capture state">
                <span className="prompt-state custom">persisted rows</span>
                <span className="prompt-state default">preview capture</span>
              </div>
            </div>

            <section className="prompt-replay-form prompt-diagnostics-filters" aria-label="Diagnostic filters">
              <label>
                <span>LLM workflow</span>
                <select
                  data-testid="prompt-diagnostics-workflow"
                  value={selectedWorkflow}
                  onChange={(event) => updateSearch({
                    workflow: event.target.value,
                    diagnostic: null,
                  })}
                >
                  <option value="all">All workflows</option>
                  {templates.map((template) => (
                    <option key={template.workflow} value={template.workflow}>
                      {template.title}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                <span>Transcript session</span>
                <select
                  data-testid="prompt-diagnostics-session"
                  value={sessionId}
                  onChange={(event) => updateSearch({
                    session: event.target.value || null,
                    diagnostic: null,
                  })}
                >
                  <option value="">All sessions</option>
                  {sessions?.map((session) => (
                    <option key={session.id} value={session.id}>
                      {session.title} · {formatTranscriptCount(session)}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                <span>Source</span>
                <select
                  data-testid="prompt-diagnostics-source"
                  value={source}
                  onChange={(event) => updateSearch({
                    source: event.target.value === "all" ? null : event.target.value,
                    diagnostic: null,
                  })}
                >
                  <option value="all">All sources</option>
                  <option value="live">Live</option>
                  <option value="replay">Replay</option>
                  <option value="repair">Repair</option>
                  <option value="audience">Audience</option>
                </select>
              </label>
              <label>
                <span>Status</span>
                <select
                  data-testid="prompt-diagnostics-status"
                  value={status}
                  onChange={(event) => updateSearch({
                    status: event.target.value === "all" ? null : event.target.value,
                    diagnostic: null,
                  })}
                >
                  <option value="all">All statuses</option>
                  <option value="pending">Pending</option>
                  <option value="success">Success</option>
                  <option value="error">Error</option>
                </select>
              </label>
            </section>

            <section className="prompt-replay-results">
              <div className="prompt-replay-summary">
                <Metric label="Calls" value={formatInteger(totals.calls)} />
                <Metric label="Errors" value={formatInteger(totals.errors)} tone={totals.errors > 0 ? "warn" : "default"} />
                <Metric label="Input tokens" value={formatInteger(totals.inputTokens)} />
                <Metric label="Output tokens" value={formatInteger(totals.outputTokens)} />
                <Metric label="Estimated cost" value={formatCost(totals.estimatedCostUsd)} tone="live" />
              </div>

              <WorkflowUsageRollupTable
                result={usageRollup ?? fallbackUsageRollup}
                workflowLabels={workflowLabels}
                loading={usageRollupLoading && !fallbackUsageRollup}
                fallback={usageRollup === null && fallbackUsageRollup !== null}
                error={usageRollupError}
                usageWindow={usageWindow}
                usageSort={usageSort}
                customStart={customStart}
                customEnd={customEnd}
                onWindowChange={setUsageWindow}
                onSortChange={setUsageSort}
                onCustomStartChange={setCustomStart}
                onCustomEndChange={setCustomEnd}
                onRefresh={() => void refreshUsageRollup()}
              />

              <div className="prompt-replay-grid prompt-diagnostics-grid">
                <div className="prompt-replay-checkpoints" aria-label="Diagnostic invocations">
                  {diagnostics?.map((diagnostic) => (
                    <button
                      key={diagnostic.id}
                      className={`prompt-replay-checkpoint prompt-diagnostic-row ${diagnostic.id === selectedSummary?.id ? "selected" : ""} ${diagnostic.status === "error" ? "error-row" : ""}`}
                      data-testid="prompt-diagnostic-row"
                      type="button"
                      onClick={() => updateSearch({ diagnostic: diagnostic.id })}
                    >
                      <span className="prompt-replay-checkpoint-index">{formatTime(diagnostic.startedAt)}</span>
                      <span>
                        <strong>{diagnostic.label}</strong>
                        <small>
                          {diagnostic.source} · {diagnostic.model} · {formatTokenPair(diagnostic)}
                        </small>
                      </span>
                      {diagnostic.status === "success" ? (
                        <CheckCircle aria-label="Success" weight="fill" />
                      ) : diagnostic.status === "error" ? (
                        <Warning aria-label="Error" weight="fill" />
                      ) : (
                        <ClockCounterClockwise aria-label="Pending" weight="fill" />
                      )}
                    </button>
                  ))}
                  {diagnostics && diagnostics.length === 0 && (
                    <div className="prompt-replay-empty compact">
                      No diagnostics captured for the current filters yet.
                    </div>
                  )}
                  {!diagnostics && (
                    <div className="prompt-replay-empty compact">Loading diagnostics...</div>
                  )}
                </div>

                {selectedDetail ? (
                  <DiagnosticDetailPanel detail={selectedDetail} />
                ) : (
                  <div className="prompt-replay-empty compact">
                    {selectedSummary ? "Loading diagnostic detail..." : "Select a diagnostic row to inspect details."}
                  </div>
                )}
              </div>
            </section>

            <PricingSettingsCard
              settings={pricingSettings}
              drafts={pricingDrafts}
              savingKey={savingPricingKey}
              onDraftChange={updatePricingDraft}
              onSave={(setting) => void savePricing(setting)}
            />
          </div>
        </section>
      </main>
    </div>
  );
}

function WorkflowUsageRollupTable({
  result,
  workflowLabels,
  loading,
  fallback,
  error,
  usageWindow,
  usageSort,
  customStart,
  customEnd,
  onWindowChange,
  onSortChange,
  onCustomStartChange,
  onCustomEndChange,
  onRefresh,
}: {
  result: AiDiagnosticAggregationResult | null;
  workflowLabels: Map<string, string>;
  loading: boolean;
  fallback: boolean;
  error: string | null;
  usageWindow: UsageWindow;
  usageSort: UsageSort;
  customStart: string;
  customEnd: string;
  onWindowChange: (value: UsageWindow) => void;
  onSortChange: (value: UsageSort) => void;
  onCustomStartChange: (value: string) => void;
  onCustomEndChange: (value: string) => void;
  onRefresh: () => void;
}) {
  const rows = useMemo(
    () => sortUsageRows(result?.workflows ?? [], usageSort),
    [result?.workflows, usageSort],
  );
  return (
    <section className="prompt-replay-card prompt-usage-table-card" data-testid="prompt-usage-table">
      <div className="prompt-usage-table-head">
        <div>
          <h3>Workflow token and cost rollup</h3>
          <p>
            {result
              ? `${formatDateTime(result.startTs)} to ${formatDateTime(result.endTs)} · ${formatInteger(result.scannedRowCount)} rows scanned`
              : "Choose a bounded window to compare workflow usage."}
          </p>
        </div>
        <span>{formatInteger(rows.length)} workflows</span>
      </div>
      <div className="prompt-rollup-controls" aria-label="Usage rollup controls">
        <label>
          <span>Window</span>
          <select
            data-testid="prompt-diagnostics-window"
            value={usageWindow}
            onChange={(event) => onWindowChange(event.target.value as UsageWindow)}
          >
            <option value="15m">Last 15 minutes</option>
            <option value="1h">Last hour</option>
            <option value="24h">Last 24 hours</option>
            <option value="custom">Custom (max 24h)</option>
          </select>
        </label>
        {usageWindow === "custom" && (
          <>
            <label>
              <span>From</span>
              <input
                type="datetime-local"
                value={customStart}
                onChange={(event) => onCustomStartChange(event.target.value)}
              />
            </label>
            <label>
              <span>To</span>
              <input
                type="datetime-local"
                value={customEnd}
                onChange={(event) => onCustomEndChange(event.target.value)}
              />
            </label>
          </>
        )}
        <label>
          <span>Rank by</span>
          <select value={usageSort} onChange={(event) => onSortChange(event.target.value as UsageSort)}>
            <option value="tokens">Total tokens</option>
            <option value="cost">Known cost</option>
            <option value="calls">Calls</option>
          </select>
        </label>
        <button className="btn ghost sm" type="button" disabled={loading} onClick={onRefresh}>
          <ArrowsClockwise aria-hidden="true" weight="bold" />
          {loading ? "Refreshing..." : "Refresh"}
        </button>
      </div>
      {error && (
        <div className="prompt-message warn" role="status">
          <Warning aria-hidden="true" weight="fill" />
          {error}
        </div>
      )}
      {result && (result.truncated || fallback) && (
        <div className="prompt-rollup-sample-note" role="status">
          {fallback
            ? `Fallback sample: the current filtered list contains ${formatInteger(result.includedRowCount)} of at most ${formatInteger(result.maxScannedRowCount)} recent rows.`
            : `Bounded sample: stopped after ${formatInteger(result.scannedRowCount)} rows; totals are lower bounds.`}
        </div>
      )}
      {loading ? (
        <div className="prompt-replay-empty compact">Loading workflow rollup...</div>
      ) : rows.length === 0 ? (
        <div className="prompt-replay-empty compact">No diagnostic usage in this window.</div>
      ) : (
        <div className="prompt-usage-table-wrap">
          <table className="prompt-usage-table">
            <thead>
              <tr>
                <th>Workflow</th>
                <th>Calls</th>
                <th>Logical ops</th>
                <th>Input / cached</th>
                <th>Output / reasoning</th>
                <th>Tokens</th>
                <th>Prompt chars</th>
                <th>Duration</th>
                <th>Known cost</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.workflow}>
                  <td>
                    <strong>{workflowLabels.get(row.workflow) ?? row.workflow}</strong>
                    <small>{row.workflow}</small>
                    <small>{formatBreakdowns(row)}</small>
                  </td>
                  <td className="numeric">
                    <strong>{formatInteger(row.calls)}</strong>
                    <small className={row.errorCount > 0 ? "warn" : ""}>
                      {row.successCount} ok · {row.errorCount} err · {row.pendingCount} pending
                    </small>
                  </td>
                  <td className="numeric">
                    <strong>{formatInteger(row.logicalOperationCount)}</strong>
                    <small>{formatInteger(row.logicalOperationTotalTokens)} combined tokens</small>
                  </td>
                  <td className="numeric">
                    <strong>{formatInteger(row.inputTokens)}</strong>
                    <small>{formatInteger(row.cachedInputTokens)} cached</small>
                  </td>
                  <td className="numeric">
                    <strong>{formatInteger(row.outputTokens)}</strong>
                    <small>{formatInteger(row.reasoningTokens)} reasoning</small>
                  </td>
                  <td className="numeric">
                    <strong>{formatInteger(row.totalTokens)}</strong>
                    <small>{formatAverageMax(row.averageTokensPerCall, row.maxTokensPerCall)}</small>
                  </td>
                  <td className="numeric">
                    <strong>{formatInteger(row.averagePromptCharacters)}</strong>
                    <small>max {formatInteger(row.maxPromptCharacters)}</small>
                  </td>
                  <td className="numeric">
                    <strong>{formatDuration(row.averageDurationMs)}</strong>
                    <small>max {formatDuration(row.maxDurationMs)}</small>
                  </td>
                  <td className="numeric">
                    <strong>{formatCost(row.knownCostUsd)}</strong>
                    {row.unknownCostCallCount > 0 && (
                      <small className="warn">{row.unknownCostCallCount} calls unknown</small>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function DiagnosticDetailPanel({ detail }: { detail: AiDiagnosticDetail }) {
  const contextFields = diagnosticContextFields(detail);
  return (
    <article className="prompt-replay-detail" data-testid="prompt-diagnostic-detail">
      <header>
        <div>
          <p className="prompt-key">
            {detail.provider}:{detail.model} · {detail.source}
          </p>
          <h2>{detail.label}</h2>
        </div>
        <span className={`prompt-state ${detail.status === "success" ? "custom" : "default"}`}>
          {detail.status}
        </span>
      </header>

      <section className="prompt-replay-summary compact-summary">
        <Metric label="Duration" value={detail.durationMs === null ? "pending" : `${detail.durationMs}ms`} />
        <Metric label="Total tokens" value={formatInteger(detail.totalTokens ?? 0)} />
        <Metric label="Estimated cost" value={formatCost(detail.estimatedCostUsd)} />
        <Metric label="Capture" value={detail.debugCaptureMode} />
      </section>

      {detail.errorSummary && (
        <div className="prompt-message warn" role="alert">
          <Warning aria-hidden="true" weight="fill" />
          {detail.errorSummary}
        </div>
      )}

      {contextFields.length > 0 && (
        <section className="prompt-replay-card prompt-diagnostic-context" data-testid="prompt-diagnostic-context">
          <h3>Effective request context</h3>
          <div>
            {contextFields.map((field) => (
              <span key={field.label}>
                <small>{field.label}</small>
                <strong>{field.value}</strong>
              </span>
            ))}
          </div>
        </section>
      )}

      <TextBlock title="System prompt preview" value={detail.systemText ?? "(not captured)"} />
      <TextBlock title="User prompt preview" value={detail.promptText ?? "(not captured)"} />
      <TextBlock title="Metadata" value={detail.metadataJson ?? "(none)"} />
      <TextBlock title="Usage" value={detail.usageJson ?? usageFallback(detail)} />
      <TextBlock title="Provider metadata" value={detail.providerMetadataJson ?? "(none)"} />
      <TextBlock title="Response summary" value={detail.responseJson ?? "(none)"} />
      <TextBlock title="Error summary" value={detail.errorJson ?? "(none)"} />

      {detail.children.length > 0 && (
        <section className="prompt-replay-card">
          <h3>Linked child calls</h3>
          <div className="prompt-diagnostic-children">
            {detail.children.map((child) => (
              <div key={child.id}>
                <strong>{child.label}</strong>
                <span>{child.status} · {formatTokenPair(child)} · {formatCost(child.estimatedCostUsd)}</span>
              </div>
            ))}
          </div>
        </section>
      )}
    </article>
  );
}

function PricingSettingsCard({
  settings,
  drafts,
  savingKey,
  onDraftChange,
  onSave,
}: {
  settings: AiPricingAdminSetting[] | undefined;
  drafts: Record<string, PricingDraft>;
  savingKey: string | null;
  onDraftChange: (key: string, field: keyof PricingDraft, value: string) => void;
  onSave: (setting: AiPricingAdminSetting) => void;
}) {
  return (
    <section className="prompt-replay-card prompt-pricing-card" data-testid="prompt-pricing-card">
      <div className="prompt-pricing-head">
        <div>
          <h3>Pricing settings</h3>
          <p>Saved rates apply to future diagnostic cost estimates.</p>
        </div>
        <CurrencyDollar aria-hidden="true" weight="bold" />
      </div>
      <div className="prompt-pricing-grid">
        {settings?.map((setting) => {
          const draft = drafts[setting.key] ?? pricingSettingToDraft(setting);
          const saving = savingKey === setting.key;
          return (
            <div key={setting.key} className="prompt-pricing-row">
              <div>
                <strong>{setting.provider} · {setting.model}</strong>
                <small>{setting.overridden ? "manual override" : "default rate"}</small>
              </div>
              {setting.unit === "tokens_per_million" ? (
                <>
                  <PriceInput
                    label="Cached input / M"
                    value={draft.cachedInputUsdPerMillion}
                    onChange={(value) => onDraftChange(setting.key, "cachedInputUsdPerMillion", value)}
                  />
                  <PriceInput
                    label="Input / M"
                    value={draft.inputUsdPerMillion}
                    onChange={(value) => onDraftChange(setting.key, "inputUsdPerMillion", value)}
                  />
                  <PriceInput
                    label="Output / M"
                    value={draft.outputUsdPerMillion}
                    onChange={(value) => onDraftChange(setting.key, "outputUsdPerMillion", value)}
                  />
                </>
              ) : (
                <PriceInput
                  label="Audio / hr"
                  value={draft.audioUsdPerHour}
                  onChange={(value) => onDraftChange(setting.key, "audioUsdPerHour", value)}
                />
              )}
              <button
                className="btn ghost sm"
                type="button"
                disabled={saving}
                onClick={() => onSave(setting)}
              >
                <FloppyDisk aria-hidden="true" weight="bold" />
                {saving ? "Saving..." : "Save"}
              </button>
            </div>
          );
        })}
        {!settings && <div className="prompt-replay-empty compact">Loading pricing settings...</div>}
      </div>
    </section>
  );
}

function PriceInput({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <label className="prompt-pricing-input">
      <span>{label}</span>
      <input
        min="0"
        step="0.000001"
        type="number"
        value={value}
        onChange={(event) => onChange(event.target.value)}
      />
    </label>
  );
}

function Metric({
  label,
  value,
  tone = "default",
}: {
  label: string;
  value: string;
  tone?: "default" | "live" | "warn";
}) {
  return (
    <div className={`prompt-replay-metric ${tone}`}>
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

function TextBlock({ title, value }: { title: string; value: string }) {
  return (
    <section className="prompt-replay-card">
      <h3>{title}</h3>
      <pre>{value}</pre>
    </section>
  );
}

function summarizeDiagnostics(rows: AiDiagnosticSummary[]) {
  return rows.reduce(
    (totals, row) => ({
      calls: totals.calls + 1,
      errors: totals.errors + (row.status === "error" ? 1 : 0),
      inputTokens: totals.inputTokens + (row.inputTokens ?? 0),
      outputTokens: totals.outputTokens + (row.outputTokens ?? 0),
      estimatedCostUsd: totals.estimatedCostUsd + (row.estimatedCostUsd ?? 0),
    }),
    {
      calls: 0,
      errors: 0,
      inputTokens: 0,
      outputTokens: 0,
      estimatedCostUsd: 0,
    },
  );
}

function buildWorkflowLabelMap(templates: PromptTemplate[]): Map<string, string> {
  return new Map(templates.map((template) => [template.workflow, template.title]));
}

function sortUsageRows(
  rows: readonly AiWorkflowUsageRollup[],
  sort: UsageSort,
): AiWorkflowUsageRollup[] {
  return [...rows].sort((left, right) => {
    if (sort === "cost") return right.knownCostUsd - left.knownCostUsd || right.totalTokens - left.totalTokens;
    if (sort === "calls") return right.calls - left.calls || right.totalTokens - left.totalTokens;
    return right.totalTokens - left.totalTokens || right.calls - left.calls;
  });
}

function formatBreakdowns(row: AiWorkflowUsageRollup): string {
  const models = row.models.slice(0, 2).map((item) => `${item.key} (${item.calls})`).join(", ");
  const sources = row.sources.slice(0, 2).map((item) => `${item.key} (${item.calls})`).join(", ");
  return [models, sources].filter(Boolean).join(" · ");
}

function formatAverageMax(average: number | null, maximum: number | null): string {
  if (average === null || maximum === null) return "usage unknown";
  return `avg ${formatInteger(average)} · max ${formatInteger(maximum)}`;
}

function formatDuration(value: number | null): string {
  if (value === null) return "unknown";
  if (value < 1_000) return `${Math.round(value)}ms`;
  return `${(value / 1_000).toFixed(value < 10_000 ? 1 : 0)}s`;
}

function resolveUsageWindow(
  usageWindow: UsageWindow,
  customStart: string,
  customEnd: string,
): { startTs: number; endTs: number } {
  if (usageWindow === "custom") {
    const startTs = Date.parse(customStart);
    const endTs = Date.parse(customEnd);
    if (!Number.isFinite(startTs) || !Number.isFinite(endTs) || endTs <= startTs) {
      throw new Error("Invalid custom diagnostic window.");
    }
    if (endTs - startTs > 24 * 60 * 60 * 1_000) {
      throw new Error("Custom diagnostic windows are limited to 24 hours.");
    }
    return { startTs, endTs };
  }
  const endTs = Date.now();
  const durationMs = usageWindow === "15m"
    ? 15 * 60 * 1_000
    : usageWindow === "1h"
      ? 60 * 60 * 1_000
      : 24 * 60 * 60 * 1_000;
  return { startTs: endTs - durationMs, endTs };
}

function toDateTimeInput(value: number): string {
  const date = new Date(value);
  const pad = (part: number) => String(part).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

function diagnosticContextFields(
  detail: AiDiagnosticDetail,
): Array<{ label: string; value: string }> {
  const metadata = parseMetadata(detail.metadataJson);
  const fields: Array<{ label: string; value: string }> = [];
  const candidateCount = metadata.candidateCount;
  if (detail.parentDiagnosticId) {
    fields.push({ label: "Parent call", value: shortId(detail.parentDiagnosticId) });
  }
  if (typeof candidateCount === "number") {
    fields.push({ label: "Candidate count", value: formatInteger(candidateCount) });
    if (detail.workflow === "moderator_suggestion") {
      fields.push({
        label: "Judge mode",
        value: candidateCount > 1 ? "on · generator plus judge" : "off · single generator call",
      });
    } else if (detail.workflow === "suggestion_judge") {
      fields.push({ label: "Judge mode", value: "judge child call" });
    }
  }
  const knownFields: Array<[string, string]> = [
    ["checkpointKind", "Checkpoint"],
    ["checkpointId", "Checkpoint ID"],
    ["triggerKind", "Recap trigger"],
    ["coverageWindowId", "Coverage window"],
    ["windowOrdinal", "Window ordinal"],
    ["attempt", "Retry attempt"],
    ["attemptCount", "Retry attempt"],
    ["transcriptEntryCount", "Transcript entries"],
    ["promptTurnCount", "Prompt turns"],
    ["mergedTranscriptEntryCount", "Merged entries"],
    ["promptCompactionRatio", "Compaction ratio"],
    ["renderedPromptCharacterCount", "Rendered prompt chars"],
    ["recoveryReason", "Recovery reason"],
    ["violationCount", "Schema violations"],
  ];
  for (const [key, label] of knownFields) {
    const value = metadata[key];
    if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
      fields.push({ label, value: String(value) });
    }
  }
  return fields;
}

function parseMetadata(value: string | null): Record<string, unknown> {
  if (!value) return {};
  try {
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === "object" && !Array.isArray(parsed)
      ? parsed as Record<string, unknown>
      : {};
  } catch {
    return {};
  }
}

function pricingSettingToDraft(setting: AiPricingAdminSetting): PricingDraft {
  return {
    inputUsdPerMillion: setting.inputUsdPerMillion?.toString() ?? "",
    cachedInputUsdPerMillion: setting.cachedInputUsdPerMillion?.toString() ?? "",
    outputUsdPerMillion: setting.outputUsdPerMillion?.toString() ?? "",
    audioUsdPerHour: setting.audioUsdPerHour?.toString() ?? "",
  };
}

function parsePricingNumber(value: string): number {
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed < 0) throw new Error("Enter a non-negative pricing value.");
  return parsed;
}

function usageFallback(detail: AiDiagnosticDetail): string {
  return JSON.stringify(
    {
      inputTokens: detail.inputTokens,
      cachedInputTokens: detail.cachedInputTokens,
      outputTokens: detail.outputTokens,
      reasoningTokens: detail.reasoningTokens,
      totalTokens: detail.totalTokens,
    },
    null,
    2,
  );
}

function normalizeStatus(value: string | null): StatusFilter {
  return value === "pending" || value === "success" || value === "error" ? value : "all";
}

function normalizeSource(value: string | null): SourceFilter {
  return value === "live" || value === "replay" || value === "repair" || value === "audience"
    ? value
    : "all";
}

function formatTranscriptCount(session: PromptReplaySessionSummary): string {
  const count = session.transcriptEntryCount ?? 0;
  const suffix = session.transcriptEntryCountIsApproximate ? "+" : "";
  return `${count}${suffix} transcript lines`;
}

function formatTokenPair(diagnostic: AiDiagnosticSummary): string {
  const input = diagnostic.inputTokens ?? 0;
  const output = diagnostic.outputTokens ?? 0;
  return `${formatInteger(input)} in / ${formatInteger(output)} out`;
}

function formatTime(value: number): string {
  return new Date(value).toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}

function formatDateTime(value: number): string {
  return new Date(value).toLocaleString([], {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatInteger(value: number): string {
  return Math.round(value).toLocaleString();
}

function formatCost(value: number | null): string {
  if (value === null) return "-";
  if (value === 0) return "$0.00";
  return `$${value.toFixed(value < 0.01 ? 6 : 4)}`;
}

function shortId(value: string): string {
  return value.length <= 10 ? value : `${value.slice(0, 6)}...${value.slice(-4)}`;
}
